import { GameEvent } from '@/types'
import { request } from '@/utils/requests/request'

import { MessageAction, OutcomingMessage } from '../main-frame/message-types'
import { createIdbApi } from '../safe-storage'

const SAFE_STORAGE_TIMEOUT = 5000 // 5s
const waitTimeout = <T>(msg: T) => new Promise<T>((res) => setTimeout(() => res(msg), SAFE_STORAGE_TIMEOUT))
type RequestParams = Parameters<typeof request>
const requestWithTimeout = (url: RequestParams[0], opts?: RequestParams[1]) =>
    Promise.race([
        request(url, opts),
        waitTimeout({ originalResponse: undefined, data: null, errors: ['Client timeout'] }),
    ])

export type SerializableState = Record<string, string | number | boolean>

export const getCloudSaveApi = (gameId: string) => ({
    getState: async () => {
        const state = await requestWithTimeout(`/api/v1/cloud-save/games/${encodeURIComponent(gameId)}/load`)

        if (state?.errors?.length) {
            console.error(state.errors)
            return {}
        }

        return state.data.saveData
    },
    setItems: async (state: SerializableState) => {
        const resp = await requestWithTimeout(`/api/v1/cloud-save/games/${encodeURIComponent(gameId)}/save`, {
            method: 'POST',
            body: JSON.stringify({ saveData: state }),
        })

        return !resp.errors?.length
    },
})

export const createCloudSave = (gameId: string, gameHru: string) => {
    const { getState, setItems } = getCloudSaveApi(gameId)

    const messageHandler = async (event: GameEvent) => {
        const createResponse = (action: MessageAction, payload: SerializableState) =>
            ({
                type: 'cloud-save',
                originalMessageEvent: event as GameEvent,
                payload,
                action,
            }) as OutcomingMessage
        switch (event.data.action) {
            case MessageAction.getCloudSaveState: {
                let state = await getState()

                // If cloud save is empty, try to get state from indexedDB
                if (Object.keys(state).length === 0) {
                    const { getState: getIdbState } = createIdbApi(gameHru)
                    const localState = await getIdbState()
                    state = localState
                }
                return createResponse(MessageAction.cloudSaveStateResponse, { state })
            }
            case MessageAction.setCloudSaveState: {
                try {
                    const success = await setItems(event.data.payload as SerializableState)
                    return createResponse(MessageAction.cloudSaveOperationResponse, { success })
                } catch (e) {
                    console.error(e)
                    const error = (e as Error).toString()
                    return createResponse(MessageAction.cloudSaveOperationResponse, { success: false, error })
                }
            }
            default:
        }
        return undefined
    }

    return messageHandler
}

export type CloudSaveMessageHandler = ReturnType<typeof createCloudSave>
